import { useState, useEffect } from "react";
import { CheckForm, CheckOnboardingIssue, isCheckError, CheckResponseError, CheckSignatory, CheckCompany } from "winslow-core";
import { ModalComponentWrapper } from "../../shared/ModalComponentWrapper";
import { Alert, AlertDescription } from "../../../ds2/components";
import { AppEmployeeRecordCheckForm } from "../app-employee-records/AppEmployeeRecordCheckForm";
import { apiCheckGetCompanyForm, apiCheckSubmitCompanyForm, apiCheckGetCompany } from "../../../services/PayrollService";
import toast from "react-hot-toast";
import { toastFail, toastSuccess } from "../../../config";
import { checkGenerateCompanyFormDefaults, checkGenerateFormDefaults } from "./checkFormDefaults";

type ModalCheckFormEditorProps = {
    isOpen: boolean;
    checkCompany: CheckCompany | null;
    checkSignatory: CheckSignatory | null;
    ein: string | null;
    filingAuthorization: CheckOnboardingIssue | null;
    onClose: () => void;
    onSuccess?: () => void;
};

export const ModalCheckFormEditor = ({
    isOpen,
    checkCompany,
    checkSignatory,
    ein,
    filingAuthorization,
    onClose,
    onSuccess,
}: ModalCheckFormEditorProps) => {
    const [selectedForm, setSelectedForm] = useState<CheckForm | null>(null);
    const [loadingFormDetails, setLoadingFormDetails] = useState<boolean>(false);
    const [submittingForm, setSubmittingForm] = useState<boolean>(false);
    const [checkResponseError, setCheckResponseError] = useState<CheckResponseError | undefined>(undefined);

    // Load form details when modal opens
    useEffect(() => {
        if (isOpen && checkCompany && checkSignatory && ein && filingAuthorization) {
            loadFormDetails();
        } else {
            // Reset state when modal closes
            setSelectedForm(null);
            setCheckResponseError(undefined);
        }
    }, [isOpen, checkCompany, checkSignatory, ein, filingAuthorization]);

    const loadFormDetails = async () => {
        if (!checkCompany || !checkSignatory || !ein || !filingAuthorization) {
            return;
        }

        setLoadingFormDetails(true);
        setCheckResponseError(undefined);

        try {
            const apiResponse = await apiCheckGetCompanyForm(checkCompany.id, filingAuthorization.id);
            const theForm = apiResponse?.data;
            setCheckResponseError(apiResponse?.checkResponseError);

            if (!apiResponse || isCheckError(apiResponse) || !theForm) {
                toast("Error loading form details", toastFail);
                return;
            }

            setSelectedForm(theForm);
        } catch (error) {
            console.error("[ModalCheckFormEditor] Error loading form details:", error);
            toast("Error loading form details", toastFail);
        } finally {
            setLoadingFormDetails(false);
        }
    };

    const handleSubmitCheckForm = async (formId: string, parameters: Array<{ name: string; value: string }>) => {
        if (!checkCompany || !checkSignatory || !ein || submittingForm) {
            toast("Error submitting form", toastFail);
            return;
        }

        console.error("[ModalCheckFormEditor] handleSubmitCheckForm===============================================");
        console.error("[ModalCheckFormEditor] parameters: ", parameters);

        // if (!!true) {   return; }

        setSubmittingForm(true);
        setCheckResponseError(undefined);

        try {
            const apiResponse = await apiCheckSubmitCompanyForm(checkCompany.id, formId, parameters);
            const result = apiResponse?.data;
            setCheckResponseError(apiResponse?.checkResponseError);

            if (!apiResponse || isCheckError(apiResponse)) {
                toast("Error submitting form", toastFail);
                return;
            }

            toast("Form submitted successfully", toastSuccess);
            
            // Refresh company data if callback provided
            if (onSuccess) {
                onSuccess();
            }
            
            onClose();
        } finally {
            setSubmittingForm(false);
        }
    };

    console.log("[ModalCheckFormEditor] checkCompany: ", checkCompany);
    console.log("[ModalCheckFormEditor] checkSignatory: ", checkSignatory);
    console.log("[ModalCheckFormEditor] ein: ", ein);
    console.log("[ModalCheckFormEditor] filingAuthorization: ", filingAuthorization);
    console.log("[ModalCheckFormEditor] selectedForm: ", selectedForm);

    return (
        <ModalComponentWrapper
            isOpen={isOpen}
            onClose={onClose}
            style={{ width: "90%", maxWidth: "900px", maxHeight: "90vh", overflow: "auto" }}
        >
            <div style={{ padding: "24px" }}>
                {checkResponseError && (
                    <Alert variant="warning" style={{ marginBottom: "16px" }}>
                        <AlertDescription>
                            {checkResponseError.errorMessage || "An error occurred"}
                        </AlertDescription>
                    </Alert>
                )}

                {loadingFormDetails ? (
                    <div style={{ padding: "40px", textAlign: "center" }}>
                        Loading form details...
                    </div>
                ) : selectedForm && filingAuthorization ? (
                    <AppEmployeeRecordCheckForm
                        checkForm={selectedForm}
                        defaultValues={checkCompany && checkSignatory && ein ? 
                            checkGenerateFormDefaults(selectedForm, checkCompany, checkSignatory, ein) : undefined}
                        showTitle={true}
                        disabled={submittingForm}
                        isSubmitted={filingAuthorization.status === "completed"}
                        onSubmit={(parameters) => {
                            handleSubmitCheckForm(selectedForm.id, parameters);
                        }}
                    />
                ) : (
                    <Alert variant="warning">
                        <AlertDescription>
                            Form not found.
                        </AlertDescription>
                    </Alert>
                )}
            </div>
        </ModalComponentWrapper>
    );
};

